import { useTheme } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import React, { useId } from 'react';
import type { CustomSvgIconsProps } from './CustomSvgIcons.types';

const PATH_1 =
  'M 493.144531 211.125 C 528.917969 211.125 563.628906 218.140625 596.3125 231.984375 C 613.773438 239.375 630.449219 248.664062 645.933594 259.621094 L 642.621094 261.3125 L 600.121094 282.992188 C 567.671875 264.949219 530.710938 255.417969 493.152344 255.417969 C 492.582031 255.417969 492.007812 255.417969 491.4375 255.421875 C 462.5625 255.636719 434.441406 261.398438 407.863281 272.546875 C 381.292969 283.6875 357.460938 299.707031 337.03125 320.160156 C 295.332031 361.917969 272.363281 417.433594 272.363281 476.476562 C 272.363281 486.089844 272.996094 495.804688 274.238281 505.351562 L 274.636719 508.40625 L 277.375 507.007812 L 644.59375 319.664062 L 720.625 280.878906 L 806.746094 236.941406 L 806.746094 286.652344 L 718.027344 331.914062 L 674.527344 354.109375 L 287.4375 551.589844 L 285.703125 552.570312 L 265.90625 562.671875 L 245.820312 572.921875 L 245.777344 572.84375 L 243.972656 573.765625 L 179.546875 606.628906 L 179.546875 556.917969 L 201.324219 545.804688 C 219.613281 536.480469 230.429688 517.199219 228.886719 496.683594 C 228.386719 489.996094 228.132812 483.195312 228.132812 476.476562 C 228.132812 440.660156 235.140625 405.90625 248.960938 373.179688 C 262.3125 341.582031 281.421875 313.207031 305.75 288.84375 C 330.078125 264.484375 358.421875 245.351562 389.984375 231.984375 C 422.671875 218.140625 457.378906 211.125 493.144531 211.125';

const PATH_2 =
  'M 806.75 353.070312 L 806.75 402.789062 L 784.957031 413.902344 C 766.660156 423.234375 755.839844 442.523438 757.398438 463.039062 C 757.910156 469.828125 758.171875 476.6875 758.171875 483.425781 C 758.171875 519.242188 751.160156 553.996094 737.335938 586.71875 C 723.988281 618.3125 704.882812 646.691406 680.550781 671.058594 C 656.207031 695.425781 627.867188 714.554688 596.3125 727.917969 C 563.621094 741.761719 528.910156 748.777344 493.152344 748.777344 C 457.390625 748.777344 422.683594 741.761719 389.988281 727.917969 C 372.460938 720.5 355.722656 711.164062 340.1875 700.144531 L 384.210938 677.691406 L 385.96875 676.792969 C 418.46875 694.910156 455.507812 704.484375 493.164062 704.484375 C 493.679688 704.484375 494.203125 704.484375 494.71875 704.480469 C 523.621094 704.28125 551.765625 698.527344 578.371094 687.378906 C 604.96875 676.234375 628.820312 660.207031 649.265625 639.738281 C 690.964844 597.984375 713.933594 542.472656 713.933594 483.425781 C 713.933594 473.789062 713.296875 464.007812 712.042969 454.359375 L 711.644531 451.304688 L 708.90625 452.703125 L 341.316406 640.234375 L 265.285156 679.019531 L 179.554688 722.757812 L 179.554688 673.054688 L 267.882812 627.984375 L 311.378906 605.792969 Z';

export const StellarIcon = ({
  shouldbegradient,
  transitionduration = 0.4,
  prefix,
  solidColor,
  iconType = 'normal',
  gradientColor,
  ...props
}: CustomSvgIconsProps): React.ReactElement => {
  const id = useId();
  const currColorId = `${prefix}_stellarCurrentColorGradient_${id}`;
  const newColorId = `${prefix}_stellarNewColorGradient_${id}`;

  const theme = useTheme();
  const isLight = theme.palette.type === 'light';
  const colorsDark = {
    base: {
      start: theme.palette?.greyColors?.PLUS_8,
      stop: theme.palette.greyColors?.PLUS_2,
    },
    hover: {
      start: '#FFD900',
      stop: '#FFA800',
    },
  };
  const colorsLight = {
    base: {
      start: theme.palette?.greyColors?.PLUS_4,
      stop: theme.palette.greyColors?.PLUS_2,
    },
    hover: {
      start: '#FFD900',
      stop: '#FFA800',
    },
  };
  const svgColors = isLight ? colorsLight : colorsDark;

  const paths = (
    <>
      <path d={PATH_1} fillRule="nonzero" />
      <path d={PATH_2} fillRule="nonzero" />
    </>
  );

  return (
    <SvgIcon {...props} viewBox="0 0 960 960">
      {solidColor ? (
        paths
      ) : (
        <>
          <defs>
            <linearGradient
              style={{ transition: `${transitionduration}s` }}
              id={currColorId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%">
              <stop offset="0" stopColor={svgColors.base.start} />
              <stop offset="1" stopColor={svgColors.base.stop} />
            </linearGradient>
            <linearGradient id={newColorId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0" stopColor={svgColors.hover.start} />
              <stop offset="1" stopColor={svgColors.hover.stop} />
            </linearGradient>
          </defs>
          <g style={{ fill: `url("#${currColorId}")` }}>{paths}</g>
          <g
            style={{
              fill: `url("#${newColorId}")`,
              transition: `${transitionduration}s`,
              opacity: shouldbegradient ? 1 : 0,
            }}>
            {paths}
          </g>
        </>
      )}
    </SvgIcon>
  );
};
